import React, { useEffect, useMemo, useState } from 'react';
import { AccessEvaluationVisual } from './InteractiveAnalyticsVisuals';

import { INTERACTIVE_ANALYTICS } from '../../../../content/staticAssets';

const BASE = INTERACTIVE_ANALYTICS.governanceTriangleBase;
const FRAME_COUNT = 8;
const LOOP_SECONDS = 8.5;

const FRAMES = Array.from({ length: FRAME_COUNT }, (_, i) => `${BASE}/frame-${String(i + 1).padStart(2, '0')}.png`);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function useInView(threshold = 0.2) {
  const ref = React.useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin: '0px 0px -6% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

function useImagePreload(sources) {
  const [states, setStates] = useState(() => sources.map(() => 'pending'));

  useEffect(() => {
    setStates(sources.map(() => 'pending'));

    const cleanups = sources.map((src, index) => {
      const img = new Image();
      const mark = (status) => {
        setStates((prev) => {
          const next = [...prev];
          next[index] = status;
          return next;
        });
      };

      img.onload = () => mark('ok');
      img.onerror = () => mark('error');
      img.src = src;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [sources]);

  const readyCount = states.filter((s) => s === 'ok').length;
  const assetsReady = readyCount === sources.length;
  const assetsSettled = states.every((s) => s !== 'pending');
  const assetsFailed = assetsSettled && !assetsReady;
  const firstReadyIndex = states.findIndex((s) => s === 'ok');

  return { assetsReady, assetsFailed, firstReadyIndex };
}

/**
 * Governed access triangle: eight transparent PNG frames with smooth crossfade loop.
 * Shield visits data → semantic → reporting → all-governed center state.
 */
export default function GovernanceTriangleSequence({
  alt = 'Governed access across data, semantic, and reporting surfaces with a shield traveling each layer',
}) {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView();
  const frames = useMemo(() => FRAMES, []);
  const { assetsReady, assetsFailed, firstReadyIndex } = useImagePreload(frames);

  const finalSrc = frames[frames.length - 1];
  const previewSrc = firstReadyIndex >= 0 ? frames[firstReadyIndex] : frames[0];

  if (assetsFailed) {
    return <AccessEvaluationVisual />;
  }

  const sceneStyle = {
    aspectRatio: '491 / 240',
    '--gov-loop-duration': `${LOOP_SECONDS}s`,
  };

  return (
    <div ref={ref} className="gov-wrap w-full">
      <div className="sr-only" role="img" aria-label={alt} />

      {!assetsReady ? (
        <div className="gov-scene relative mx-auto w-full" style={sceneStyle} aria-hidden="true">
          {firstReadyIndex >= 0 ? (
            <img className="gov-img-static" src={previewSrc} alt="" draggable="false" />
          ) : (
            <div className="gov-scene gov-scene--loading h-full">
              <AccessEvaluationVisual />
            </div>
          )}
        </div>
      ) : reducedMotion ? (
        <div className="gov-scene relative mx-auto w-full" style={sceneStyle} aria-hidden="true">
          <img className="gov-img-static" src={finalSrc} alt="" draggable="false" />
        </div>
      ) : (
        <div
          className={`gov-scene gov-scene--sequence relative mx-auto w-full ${inView ? 'gov-scene--playing' : ''}`}
          style={sceneStyle}
          aria-hidden="true"
        >
          {frames.map((src, index) => (
            <img
              key={src}
              className={`gov-img gov-frame gov-frame-${index + 1}`}
              src={src}
              alt=""
              draggable="false"
            />
          ))}
        </div>
      )}
    </div>
  );
}
